"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Textarea } from "@/components/ui/textarea";
import VoiceCommentInput from "@/components/voice/VoiceCommentInput";
import { createSupportTicket } from "@/server-actions/support/actions";
import { useSession } from "next-auth/react";
import { useState, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  Paperclip,
  X,
  Upload,
  MessageCircle,
  HelpCircle,
  Bug,
  Lightbulb,
  ArrowLeft,
  Send,
  FileText,
  AlertCircle,
} from "lucide-react";
import { AssemblyContextProvider } from "@/context/SimpleVoiceInput";
import { MicrophoneContextProvider as AssemblyMicrophoneContextProvider } from "@/context/MicrophoneAssembly";

// Category options with consistent icons
const categories = [
  {
    id: "help",
    value: "confusion",
    label: "Ask a question",
    description: "How do I...?",
    icon: HelpCircle,
    color: "blue",
    prompt: "What would you like to know?",
  },
  {
    id: "bug",
    value: "ui-bug",
    label: "Report a problem",
    description: "Something's not working right",
    icon: Bug,
    color: "red",
    prompt: "What went wrong?",
    showUrgency: true,
  },
  {
    id: "feature",
    value: "feature",
    label: "Share an idea",
    description: "Suggest an improvement",
    icon: Lightbulb,
    color: "amber",
    prompt: "What would make this better?",
  },
  {
    id: "other",
    value: "other",
    label: "Say hello",
    description: "Feedback, praise, or anything else",
    icon: MessageCircle,
    color: "gray",
    prompt: "What's on your mind?",
  },
];

export default function SupportDialog() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("welcome"); // welcome, compose
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [message, setMessage] = useState("");
  const [urgency, setUrgency] = useState("normal"); // normal, urgent
  const [partialTranscription, setPartialTranscription] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const { data: session } = useSession();
  const { toast } = useToast();

  const resetForm = () => {
    setView("welcome");
    setSelectedCategory(null);
    setMessage("");
    setUrgency("normal");
    setPartialTranscription("");
    setIsRecording(false);
    setErrors({});
    setAttachments([]);
  };

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setView("compose");
  };

  const handleFileSelect = (files) => {
    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "video/mp4",
      "video/mpeg",
      "video/quicktime",
      "video/x-msvideo",
      "video/webm",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const allowedExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".webp",
      ".mp4",
      ".mpeg",
      ".mov",
      ".avi",
      ".webm",
      ".pdf",
      ".doc",
      ".docx",
    ];

    const validFiles = [];
    const rejectedFiles = [];

    Array.from(files).forEach((file) => {
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
      if (file.size > maxSize) {
        rejectedFiles.push(`${file.name} (too large)`);
      } else if (
        !allowedTypes.includes(file.type) ||
        !allowedExtensions.includes(ext)
      ) {
        rejectedFiles.push(`${file.name} (unsupported)`);
      } else {
        validFiles.push(file);
      }
    });

    if (rejectedFiles.length > 0) {
      toast({
        variant: "destructive",
        title: "Some files weren't added",
        description: rejectedFiles.join(", "),
      });
    }

    const newAttachments = validFiles.map((file) => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    setAttachments((prev) => {
      const combined = [...prev, ...newAttachments];
      if (combined.length > 5) {
        toast({
          title: "Max 5 files",
          description: "Some files weren't added.",
        });
      }
      return combined.slice(0, 5);
    });
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFileSelect(e.dataTransfer.files);
  };

  const uploadAttachments = async () => {
    if (attachments.length === 0) return [];
    try {
      const response = await fetch("/api/support-tickets/attachments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          files: attachments.map((att) => ({
            name: att.name,
            type: att.type,
            size: att.size,
          })),
        }),
      });
      if (!response.ok) throw new Error("Failed to get upload URLs");
      const { uploadUrls } = await response.json();

      const uploadPromises = attachments.map(async (attachment, index) => {
        const {
          reservationId,
          uploadUrl,
          fileUrl,
          fileName,
          fileType,
          fileSize,
        } = uploadUrls[index];
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": fileType },
          body: attachment.file,
        });
        if (!uploadResponse.ok) throw new Error(`Failed to upload ${fileName}`);
        return { reservationId, fileName, fileUrl, fileSize, fileType };
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Error uploading attachments:", error);
      throw error;
    }
  };

  const handlePartialTranscription = (transcript, recording) => {
    setPartialTranscription(transcript);
    setIsRecording(recording);
    if (!recording && !transcript) {
      setTimeout(() => setPartialTranscription(""), 100);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!message.trim()) {
      setErrors({ message: "Please describe what's on your mind" });
      return;
    }

    if (!session?.user?.id || !session?.user?.workspaceId) {
      setErrors({ submit: "Session expired. Please refresh and try again." });
      return;
    }

    setIsSubmitting(true);

    try {
      const uploadedAttachments = await uploadAttachments();

      // Map urgency to impact
      const impact = urgency === "urgent" ? "business-critical" : "general";

      const result = await createSupportTicket({
        impact,
        category: selectedCategory?.value || "other",
        pageUrl: window.location.href,
        pageTitle: document.title,
        userId: session.user.id,
        workspaceId: session.user.workspaceId,
        message,
        attachments: uploadedAttachments,
      });

      if (result.success) {
        resetForm();
        setOpen(false);
        toast({
          title: "Message sent!",
          description: "We'll get back to you soon.",
        });
        window.open(`/support/requests/${result.ticketId}`, "_blank");
      } else {
        setErrors({
          submit: result.error || "Failed to send. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error submitting:", error);
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const renderWelcomeView = () => (
    <div className="space-y-4 py-1">
      {/* Sabine Hero Introduction */}
      <div className="relative overflow-hidden rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-amber-400/5 to-transparent pointer-events-none" />
        <div className="relative flex items-start gap-3.5 p-4 rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50/80 to-white">
          {/* Sabine Avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-[68px] h-[68px] rounded-2xl overflow-hidden shadow-md ring-2 ring-orange-200 ring-offset-1">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/sabine/sabine-v2-playback-2.jpg"
                alt="Sabine"
                className="w-full h-full object-cover object-top scale-110"
              />
            </div>
          </div>

          {/* Intro Text */}
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[15px] font-bold text-gray-900 tracking-tight">
                Hey, I'm Sabine 👋
              </span>
            </div>
            <p className="text-sm text-gray-600 leading-snug">
              Tell me what's going on — I'll help you get it sorted out quickly.
            </p>
          </div>
        </div>
      </div>

      {/* Section Label */}
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest px-0.5">
        What do you need?
      </p>

      {/* Category Options */}
      <div className="space-y-2">
        {categories.map((cat) => {
          const Icon = cat.icon;
          const colors = {
            blue: {
              bg: "bg-blue-50",
              icon: "text-blue-600",
              hoverBg: "group-hover:bg-blue-100",
            },
            red: {
              bg: "bg-red-50",
              icon: "text-red-600",
              hoverBg: "group-hover:bg-red-100",
            },
            amber: {
              bg: "bg-amber-50",
              icon: "text-amber-600",
              hoverBg: "group-hover:bg-amber-100",
            },
            gray: {
              bg: "bg-gray-100",
              icon: "text-gray-600",
              hoverBg: "group-hover:bg-gray-200",
            },
          };
          const c = colors[cat.color];

          return (
            <button
              key={cat.id}
              onClick={() => handleCategorySelect(cat)}
              className="group w-full flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-100 bg-white hover:border-orange-200 hover:bg-orange-50/30 hover:shadow-sm transition-all duration-200 text-left"
            >
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-xl ${c.bg} ${c.hoverBg} flex items-center justify-center transition-all duration-200 group-hover:scale-105`}
              >
                <Icon className={`w-5 h-5 ${c.icon}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800">
                  {cat.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {cat.description}
                </div>
              </div>
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0 transition-all duration-200">
                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-orange-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="text-center pt-1 pb-0.5">
        <Link
          href="/support/requests"
          onClick={() => setOpen(false)}
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-orange-500 transition-colors"
        >
          <FileText className="w-3.5 h-3.5" />
          View previous requests
        </Link>
      </div>
    </div>
  );

  const renderComposeView = () => {
    const Icon = selectedCategory?.icon || MessageCircle;
    const colors = {
      blue: { bg: "bg-blue-50", icon: "text-blue-600" },
      red: { bg: "bg-red-50", icon: "text-red-600" },
      amber: { bg: "bg-amber-50", icon: "text-amber-600" },
      gray: { bg: "bg-gray-100", icon: "text-gray-600" },
    };
    const c = colors[selectedCategory?.color || "gray"];

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Header with back button */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setView("welcome")}
            className="p-1.5 -ml-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div
            className={`flex items-center gap-2 px-2.5 py-1 rounded-lg ${c.bg}`}
          >
            <Icon className={`w-3.5 h-3.5 ${c.icon}`} />
            <span className="text-xs font-medium text-gray-700">
              {selectedCategory?.label}
            </span>
          </div>
        </div>

        {/* Sabine Prompt */}
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 w-7 h-7 rounded-full overflow-hidden ring-1 ring-orange-200 shadow-sm mt-0.5">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/sabine/sabine-v2-playback-2.jpg"
              alt="Sabine"
              className="w-full h-full object-cover object-top scale-110"
            />
          </div>
          <p className="text-sm text-gray-600 leading-snug pt-1">
            {selectedCategory?.prompt}
          </p>
        </div>

        {/* Message Input */}
        <div>
          <Textarea
            value={
              partialTranscription && isRecording
                ? (message ? message + " " : "") + partialTranscription
                : message
            }
            onChange={(e) => {
              if (!isRecording) {
                setMessage(e.target.value);
                setErrors((prev) => ({ ...prev, message: "" }));
              }
            }}
            placeholder="Type here..."
            className={`min-h-[100px] resize-none rounded-xl border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:ring-offset-0 text-sm ${
              errors.message ? "border-red-300" : ""
            } ${isRecording ? "bg-blue-50/50 border-blue-200" : ""}`}
            disabled={isSubmitting}
            readOnly={isRecording}
          />
          {errors.message && (
            <p className="mt-1.5 text-xs text-red-500">{errors.message}</p>
          )}
        </div>

        {/* Voice Input */}
        <div className="flex items-center gap-2">
          <VoiceCommentInput
            onTranscriptionComplete={(transcript) => {
              setMessage((prev) => (prev + " " + transcript).trim());
              setPartialTranscription("");
              setIsRecording(false);
            }}
            onPartialTranscription={handlePartialTranscription}
            disabled={isSubmitting}
            compact
          />
          <span className="text-xs text-gray-400">or speak</span>
        </div>

        {/* Urgency - Only for bugs */}
        {selectedCategory?.showUrgency && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <AlertCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-600 flex-1">
              Is this blocking your work?
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setUrgency("normal")}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  urgency === "normal"
                    ? "bg-white border border-gray-200 text-gray-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                No
              </button>
              <button
                type="button"
                onClick={() => setUrgency("urgent")}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  urgency === "urgent"
                    ? "bg-red-50 border border-red-200 text-red-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Yes
              </button>
            </div>
          </div>
        )}

        {/* Attachments */}
        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">
            Attachments{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <div
            className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
              isDragOver
                ? "border-blue-400 bg-blue-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600 mb-1">
              Drop files here or click to browse
            </p>
            <p className="text-xs text-gray-500">
              <span className="font-medium">Supported files:</span> Images (JPG,
              PNG, GIF), Videos (MP4, MOV), PDFs, Word docs
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Max 5 files, 10MB each
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.mp4,.mpeg,.mov,.avi,.webm,.pdf,.doc,.docx"
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={isSubmitting}
            />
          </div>

          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {attachments.map((att) => (
                <div
                  key={att.id}
                  className="flex items-center gap-1.5 pl-2 pr-1 py-1 bg-gray-100 rounded-md text-xs text-gray-600"
                >
                  <Paperclip className="w-3 h-3" />
                  <span className="max-w-[120px] truncate">{att.name}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(att.id)}
                    className="p-0.5 hover:bg-gray-200 rounded"
                    disabled={isSubmitting}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <Button
          type="submit"
          className="w-full bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
          disabled={isSubmitting || !message.trim()}
        >
          {isSubmitting ? (
            "Sending..."
          ) : (
            <>
              Send
              <Send className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>

        {errors.submit && (
          <p className="text-xs text-red-500 text-center">{errors.submit}</p>
        )}
      </form>
    );
  };

  return (
    <AssemblyMicrophoneContextProvider>
      <AssemblyContextProvider language="en" recordingMode="regular">
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button
              variant="default"
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-t-lg shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Help
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
            <DialogHeader className="sr-only">
              <DialogTitle>Support</DialogTitle>
            </DialogHeader>
            {view === "welcome" && renderWelcomeView()}
            {view === "compose" && renderComposeView()}
          </DialogContent>
        </Dialog>
      </AssemblyContextProvider>
    </AssemblyMicrophoneContextProvider>
  );
}
